import React, { useState } from 'react';
import { KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { commsApi } from '@/utils/commsApi';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const MIN_LENGTH = 8;

// Google-only accounts: add a password so email + password sign-in works too.
export const SetPasswordDialog = ({ open, onOpenChange, user, onSaved }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const validation = (() => {
    if (!password) return null;
    if (password.length < MIN_LENGTH) return `Use at least ${MIN_LENGTH} characters`;
    if (password.trim().toLowerCase() === (user.email || '').trim().toLowerCase()) return 'Your password cannot be your email address';
    if (confirm && confirm !== password) return 'Passwords do not match';
    return null;
  })();
  const ready = password.length >= MIN_LENGTH && confirm === password && !validation;

  const close = (next) => {
    if (!next) { setPassword(''); setConfirm(''); }
    onOpenChange(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    try {
      const { user: updated } = await commsApi.setPassword(password, confirm);
      onSaved(updated);
      toast.success('Password set. You can now sign in with your email and password too.');
      close(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not set the password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md" data-testid="set-password-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound size={18} /> Set a password</DialogTitle>
            <DialogDescription>
              Your account signs in with Google. Add a password to also sign in with <strong>{user.email}</strong>. Google sign-in keeps working.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={`At least ${MIN_LENGTH} characters`}
              autoFocus
              data-testid="set-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm password</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Type it again"
              data-testid="set-password-confirm-input"
            />
          </div>
          <p className={`text-sm min-h-5 ${validation ? 'text-red-600' : 'text-slate-500'}`} data-testid="set-password-hint">
            {validation || `Minimum ${MIN_LENGTH} characters and not your email address.`}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => close(false)} disabled={busy} data-testid="set-password-cancel">Cancel</Button>
            <Button type="submit" disabled={!ready || busy} data-testid="set-password-submit">
              {busy ? <Loader2 size={16} className="animate-spin" /> : 'Save password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
